import React from 'react';

/**
 * IncidentTool - Incident Management
 * Check and manage ServiceNow incidents by Business Unit
 */
export const IncidentTool: React.FC = () => {
  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="max-w-7xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Incident Management</h2>
          <p className="text-gray-600">Check and manage ServiceNow incidents by Business Unit</p>
        </div>

        {/* Business Unit Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">Filter by Business Unit</label>
          <div className="flex gap-2 flex-wrap">
            <button className="px-4 py-2 bg-[#0054A4] text-white rounded-lg text-sm font-medium">
              All Units
            </button>
            <button className="px-4 py-2 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium transition-colors">
              RBU (Regional)
            </button>
            <button className="px-4 py-2 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium transition-colors">
              Australian Business Unit
            </button>
            <button className="px-4 py-2 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium transition-colors">
              Refineries
            </button>
            <button className="px-4 py-2 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium transition-colors">
              Manufacturing Plants
            </button>
            <button className="px-4 py-2 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium transition-colors">
              Distribution Centers
            </button>
            <button className="px-4 py-2 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium transition-colors">
              IT Operations
            </button>
            <button className="px-4 py-2 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium transition-colors">
              Global Services
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-red-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600 font-medium uppercase mb-1">Critical</p>
                <p className="text-3xl font-bold text-red-600">3</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-white border border-orange-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-600 font-medium uppercase mb-1">High</p>
                <p className="text-3xl font-bold text-orange-600">7</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-white border border-yellow-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-yellow-600 font-medium uppercase mb-1">Medium</p>
                <p className="text-3xl font-bold text-yellow-600">12</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 font-medium uppercase mb-1">Low</p>
                <p className="text-3xl font-bold text-gray-700">18</p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Incidents List */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Active Incidents</h3>
            <button className="bg-[#0054A4] hover:bg-[#003A73] text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Incident
            </button>
          </div>

          <div className="space-y-3">
            {/* Critical Incident */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-5 hover:bg-red-100 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-red-700 bg-red-200 px-3 py-1 rounded-full">CRITICAL</span>
                  <span className="text-xs text-gray-600 font-mono">INC0012350</span>
                </div>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-200">Refineries</span>
              </div>
              <h4 className="text-base font-semibold text-gray-900 mb-2">SCADA System Connection Lost</h4>
              <p className="text-sm text-gray-600 mb-3">Critical loss of connectivity to Refinery A SCADA system. Control room unable to monitor process values.</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>Assigned: John Smith (OT Engineer)</span>
                  <span>•</span>
                  <span>Opened: 30 min ago</span>
                  <span>•</span>
                  <span>Updated: 5 min ago</span>
                </div>
                <button className="text-[#0054A4] hover:text-[#003A73] text-sm font-medium">View Details →</button>
              </div>
            </div>

            {/* High Priority Incident - Manufacturing */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-5 hover:bg-orange-100 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-orange-700 bg-orange-200 px-3 py-1 rounded-full">HIGH</span>
                  <span className="text-xs text-gray-600 font-mono">INC0012345</span>
                </div>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-200">Manufacturing Plants</span>
              </div>
              <h4 className="text-base font-semibold text-gray-900 mb-2">PLC Communication Failure - Production Line 3</h4>
              <p className="text-sm text-gray-600 mb-3">Multiple PLCs on production line 3 not responding. Assembly line stopped affecting hourly output targets.</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>Assigned: Sarah Johnson (Automation Tech)</span>
                  <span>•</span>
                  <span>Opened: 2h ago</span>
                  <span>•</span>
                  <span>Updated: 45 min ago</span>
                </div>
                <button className="text-[#0054A4] hover:text-[#003A73] text-sm font-medium">View Details →</button>
              </div>
            </div>

            {/* High Priority - Refineries */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-5 hover:bg-orange-100 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-orange-700 bg-orange-200 px-3 py-1 rounded-full">HIGH</span>
                  <span className="text-xs text-gray-600 font-mono">INC0012348</span>
                </div>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-200">Refineries</span>
              </div>
              <h4 className="text-base font-semibold text-gray-900 mb-2">Historian Database Storage Critical</h4>
              <p className="text-sm text-gray-600 mb-3">Process historian database storage at 95% capacity. Risk of losing critical operational data.</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>Assigned: Mike Chen (Systems Admin)</span>
                  <span>•</span>
                  <span>Opened: 4h ago</span>
                  <span>•</span>
                  <span>Updated: 1h ago</span>
                </div>
                <button className="text-[#0054A4] hover:text-[#003A73] text-sm font-medium">View Details →</button>
              </div>
            </div>

            {/* Medium Priority - Distribution */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 hover:bg-yellow-100 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-yellow-700 bg-yellow-200 px-3 py-1 rounded-full">MEDIUM</span>
                  <span className="text-xs text-gray-600 font-mono">INC0012340</span>
                </div>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-200">Distribution Centers</span>
              </div>
              <h4 className="text-base font-semibold text-gray-900 mb-2">Warehouse Management System Sync Issues</h4>
              <p className="text-sm text-gray-600 mb-3">WMS not syncing inventory data with ERP. Manual reconciliation required for shipping operations.</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>Assigned: Lisa Wong (Logistics IT)</span>
                  <span>•</span>
                  <span>Opened: 1d ago</span>
                  <span>•</span>
                  <span>Updated: 3h ago</span>
                </div>
                <button className="text-[#0054A4] hover:text-[#003A73] text-sm font-medium">View Details →</button>
              </div>
            </div>

            {/* Low Priority - IT Operations */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 hover:bg-gray-100 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-600 bg-gray-200 px-3 py-1 rounded-full">LOW</span>
                  <span className="text-xs text-gray-600 font-mono">INC0012335</span>
                </div>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-200">IT Operations</span>
              </div>
              <h4 className="text-base font-semibold text-gray-900 mb-2">Asset Tag Scanner Not Working</h4>
              <p className="text-sm text-gray-600 mb-3">Barcode scanner in control room not reading asset tags. Using manual entry as workaround.</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>Assigned: Tom Anderson (IT Support)</span>
                  <span>•</span>
                  <span>Opened: 2d ago</span>
                  <span>•</span>
                  <span>Updated: 1d ago</span>
                </div>
                <button className="text-[#0054A4] hover:text-[#003A73] text-sm font-medium">View Details →</button>
              </div>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600">Showing 5 of 40 incidents</p>
            <div className="flex gap-2">
              <button className="px-3 py-1 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded text-sm transition-colors">
                Previous
              </button>
              <button className="px-3 py-1 bg-[#0054A4] text-white rounded text-sm">1</button>
              <button className="px-3 py-1 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded text-sm transition-colors">2</button>
              <button className="px-3 py-1 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded text-sm transition-colors">3</button>
              <button className="px-3 py-1 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded text-sm transition-colors">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentTool;
